import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import TimeMaster from './timeMaster';
import EventSystem from './EventSystem';
import _ from 'lodash';
import './css/analogClock.css';
import GlobalFunction from './globalFunction';
import './css/webcamModule.css';

var hidden, visibilityChange;
if (typeof document.hidden !== 'undefined') {
    hidden = 'hidden'; visibilityChange = 'visibilitychange';
} else if (typeof document.msHidden !== 'undefined') {
    hidden = 'msHidden'; visibilityChange = 'msvisibilitychange';
} else if (typeof document.webkitHidden !== 'undefined') {
    hidden = 'webkitHidden'; visibilityChange = 'webkitvisibilitychange';
}

const RADIUS_SHRINK = 0.95;
const HOUR_COLOUR = '#1D8A8A';
const MINUTE_COLOUR = '#1D8A8A';

export default function AnalogClockControlModule({ parameters, onSendMessage, mode, enableModuleLogging, addLogEntry }) {
    const eventId = parameters.config.event_id;

    const [clockLocalOffset, setClockLocalOffset] = useState(() => {
        if (parameters.config.currentTimeMaster === 'ME') return 0;
        const v = localStorage.getItem('clockLocalOffset_' + eventId);
        return v === null ? 0 : parseFloat(v);
    });
    const [broadcastDelay] = useState(() => {
        const v = localStorage.getItem('broadcastDelay_' + eventId);
        return v === null ? 0 : parseFloat(v);
    });
    const [inDelay, setInDelay] = useState(() => {
        const v = localStorage.getItem('inDelay_' + eventId);
        return v === null ? true : JSON.parse(v);
    });
    const [showTimeMaster, setShowTimeMaster] = useState(false);
    const [tabHasFocus, setTabHasFocus] = useState(true);
    const [canvasDiameter, setCanvasDiameter] = useState(0);
    const [containerWidth, setContainerWidth] = useState(0);
    const [containerHeight, setContainerHeight] = useState(0);
    const [mounted, setMounted] = useState(false);
    const [dow, setDow] = useState('');
    const [dowFontSize, setDowFontSize] = useState(0);
    const [calDate, setCalDate] = useState('');
    const [calDateFontSize, setCalDateFontSize] = useState(0);
    const [tiwPart1, setTiwPart1] = useState('');
    const [part1FontSize, setPart1FontSize] = useState(0);

    const containerRef = useRef(null);
    const clockCanvasRef = useRef(null);
    const hourCanvasRef = useRef(null);
    const minuteCanvasRef = useRef(null);
    const topCanvasRef = useRef(null);
    const secondsCanvasRef = useRef(null);
    const secondHandRef = useRef(null);
    const secondHandCircleRef = useRef(null);
    const topLocationDivRef = useRef(null);
    const botLocationDivRef = useRef(null);
    const fontSizeCheckerPreRef = useRef(null);
    const fontSizeCheckerWrapRef = useRef(null);

    const frameIDRef = useRef(0);
    const radiusOffsetRef = useRef(0);
    const hourCanvasContextRef = useRef(null);
    const minuteCanvasContextRef = useRef(null);
    const mountedRef = useRef(false);

    // liveRef holds values read by EventSystem callbacks and rAF loop (stale-closure-free)
    const liveRef = useRef({ clockLocalOffset: 0, canvasDiameter: 0, config: parameters.config, timeInWords: '' });
    liveRef.current.clockLocalOffset = clockLocalOffset;
    liveRef.current.canvasDiameter = canvasDiameter;
    liveRef.current.config = parameters.config;

    // Store latest drawing helpers in refs so refreshClock (subscribed once) can call current versions
    const displayTimeInWordsRef = useRef(null);
    const displayDateInWordsRef = useRef(null);
    displayTimeInWordsRef.current = displayTimeInWords;
    displayDateInWordsRef.current = displayDateInWords;

    useEffect(() => {
        enableModuleLogging();
        document.addEventListener(visibilityChange, checkFocus, true);
        EventSystem.subscribe('ClockModule', respondToMessage);
        const debouncedResize = _.debounce(screenResized, 250);
        window.addEventListener('resize', debouncedResize);

        if (mode !== 'setup') {
            EventSystem.subscribe('1000msHeartbeat', refreshClock);
            const cw = containerRef.current.clientWidth;
            const ch = containerRef.current.clientHeight;
            const cd = cw <= ch ? cw : ch;
            liveRef.current.canvasDiameter = cd;
            setContainerWidth(cw);
            setContainerHeight(ch);
            setCanvasDiameter(cd);
        }

        return () => {
            window.cancelAnimationFrame(frameIDRef.current);
            mountedRef.current = false;
            addLogEntry('Unmounting: ' + frameIDRef.current);
            EventSystem.unsubscribe('1000msHeartbeat', refreshClock);
            EventSystem.unsubscribe('ClockModule', respondToMessage);
            document.removeEventListener(visibilityChange, checkFocus, true);
            window.removeEventListener('resize', debouncedResize);
        };
    }, []);

    // Draw clock after canvasDiameter is updated in DOM
    useEffect(() => {
        if (canvasDiameter === 0) return;
        mountedRef.current = true;
        setMounted(true);
        displayAnalogClock();
    }, [canvasDiameter]);

    // CWRP: reset offset when becoming time master
    const firstCWRPRender = useRef(true);
    useEffect(() => {
        if (firstCWRPRender.current) { firstCWRPRender.current = false; return; }
        if (parameters.config.currentTimeMaster === 'ME') {
            setClockLocalOffset(0);
            liveRef.current.clockLocalOffset = 0;
        }
    }, [parameters.config.currentTimeMaster]);

    function checkFocus() {
        setTabHasFocus(!document[hidden]);
    }

    function respondToMessage(data) {
        if (data.action === 'set_delay') {
            const stored = localStorage.getItem('inDelay_' + eventId);
            setInDelay(stored === null ? true : JSON.parse(stored));
        }
    }

    function screenResized() {
        window.cancelAnimationFrame(frameIDRef.current);
        if (!containerRef.current) return;
        addLogEntry('Screen resized: ' + containerRef.current.clientHeight + '/' + containerRef.current.clientWidth);
        const cw = containerRef.current.clientWidth;
        const ch = containerRef.current.clientHeight;
        const cd = cw <= ch ? cw : ch;
        liveRef.current.canvasDiameter = cd;
        setContainerWidth(cw);
        setContainerHeight(ch);
        setCanvasDiameter(cd); // triggers canvasDiameter useEffect → displayAnalogClock
    }

    function displayAnalogClock() {
        const cd = liveRef.current.canvasDiameter;
        const clockRadius = (cd / 2) * RADIUS_SHRINK;
        radiusOffsetRef.current = (cd / 2) - clockRadius;

        const canvasCtx = clockCanvasRef.current.getContext('2d');
        hourCanvasContextRef.current = hourCanvasRef.current.getContext('2d');
        minuteCanvasContextRef.current = minuteCanvasRef.current.getContext('2d');
        const topCtx = topCanvasRef.current.getContext('2d');

        canvasCtx.translate(clockRadius + radiusOffsetRef.current, clockRadius + radiusOffsetRef.current);
        topCtx.translate(clockRadius + radiusOffsetRef.current, clockRadius + radiusOffsetRef.current);
        drawFace(canvasCtx, topCtx, clockRadius);
        drawNumbers(canvasCtx, clockRadius);
        topCtx.translate(-clockRadius - radiusOffsetRef.current, -clockRadius - radiusOffsetRef.current);
        canvasCtx.translate(-clockRadius - radiusOffsetRef.current, -clockRadius - radiusOffsetRef.current);
        drawClockBackground();

        if (liveRef.current.config.other_config.secondsDisplay === 'edge') {
            frameIDRef.current = window.requestAnimationFrame(drawOutsideSeconds);
        }
    }

    function drawClockBackground() {
        const cd = liveRef.current.canvasDiameter;
        const clockRadius = (cd / 2) * RADIUS_SHRINK;
        const ro = (cd / 2) - clockRadius;
        const ctx = secondsCanvasRef.current.getContext('2d');
        ctx.save();
        ctx.translate(clockRadius + ro, clockRadius + ro);
        ctx.beginPath();
        ctx.arc(0, 0, clockRadius, 0, 2 * Math.PI);
        ctx.fillStyle = '#333333';
        ctx.closePath();
        ctx.fill();
        ctx.restore();
    }

    function drawOutsideSeconds() {
        if (!mountedRef.current) return;
        const cd = liveRef.current.canvasDiameter;
        const clockRadius = (cd / 2) * RADIUS_SHRINK;
        const ro = (cd / 2) - clockRadius;
        const ctx = secondsCanvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, secondsCanvasRef.current.width, secondsCanvasRef.current.height);
        ctx.save();
        ctx.translate(clockRadius + ro, clockRadius + ro);
        ctx.rotate(-Math.PI / 2);
        const currentDate = GlobalFunction.getAdjustedDate(liveRef.current.clockLocalOffset, liveRef.current.config);
        const seconds = currentDate.getSeconds();
        const milliSeconds = currentDate.getMilliseconds();
        const ticks = (seconds * 1000) + milliSeconds;
        ctx.fillStyle = '#D40000AA';
        ctx.lineWidth = 1;
        ctx.lineCap = 'square';
        ctx.beginPath();
        ctx.arc(0, 0, clockRadius * 0.95, 0, (ticks / 1000) * Math.PI / 30, false);
        ctx.arc(0, 0, clockRadius * 0.9, (ticks / 1000) * Math.PI / 30, 0, true);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        if (mountedRef.current) {
            frameIDRef.current = window.requestAnimationFrame(drawOutsideSeconds);
        }
    }

    function refreshClock(updatedClockInfo) {
        if (!hourCanvasContextRef.current || !minuteCanvasContextRef.current) return;
        const cd = liveRef.current.canvasDiameter;
        const ro = radiusOffsetRef.current;
        const clockRadius = (cd / 2) - ro;
        const titleCased = toTitleCase(updatedClockInfo.timeInWords);
        if (titleCased !== liveRef.current.timeInWords) {
            liveRef.current.timeInWords = titleCased;
            if (liveRef.current.config.other_config.timeInWordsDisplayed !== '') {
                displayTimeInWordsRef.current(titleCased);
            }
            if (liveRef.current.config.other_config.dateDisplayed !== '') {
                displayDateInWordsRef.current(updatedClockInfo.currentDate);
            }
        }
        hourCanvasContextRef.current.clearRect(0, 0, cd, cd);
        minuteCanvasContextRef.current.clearRect(0, 0, cd, cd);
        hourCanvasContextRef.current.translate(clockRadius + ro, clockRadius + ro);
        minuteCanvasContextRef.current.translate(clockRadius + ro, clockRadius + ro);
        drawTime(hourCanvasContextRef.current, minuteCanvasContextRef.current, clockRadius, updatedClockInfo.currentDate);
        hourCanvasContextRef.current.translate(-clockRadius - ro, -clockRadius - ro);
        minuteCanvasContextRef.current.translate(-clockRadius - ro, -clockRadius - ro);
    }

    function displayTimeInWords(timeInWords) {
        const fontSize = GlobalFunction.getMultiLineFontSize(
            timeInWords,
            botLocationDivRef.current.clientWidth,
            botLocationDivRef.current.clientHeight,
            fontSizeCheckerWrapRef.current
        );
        setTiwPart1(timeInWords);
        setPart1FontSize(fontSize);
    }

    function displayDateInWords(currentDate) {
        const dowStr = moment(currentDate).format('dddd');
        const calDateStr = moment(currentDate).format('Do MMM');
        const dfs = GlobalFunction.getFontSize(dowStr, topLocationDivRef.current.clientWidth * 0.8, topLocationDivRef.current.clientHeight / 2, fontSizeCheckerPreRef.current);
        const cdfs = GlobalFunction.getFontSize(calDateStr, topLocationDivRef.current.clientWidth * 0.8, topLocationDivRef.current.clientHeight / 2, fontSizeCheckerPreRef.current);
        const fs = Math.min(dfs, cdfs);
        setDow(dowStr);
        setDowFontSize(fs);
        setCalDate(calDateStr);
        setCalDateFontSize(fs);
    }

    function drawFace(ctx, topCtx, radius) {
        ctx.beginPath();
        ctx.arc(0, 0, radius, 0, 2 * Math.PI);
        const grad = ctx.createRadialGradient(0, 0, radius * 0.95, 0, 0, radius * 1.05);
        grad.addColorStop(0, parameters.colours.backgroundColor);
        grad.addColorStop(0.2, '#474747');
        grad.addColorStop(0.5, '#858585');
        grad.addColorStop(0.8, '#474747');
        grad.addColorStop(1, parameters.colours.backgroundColor);
        ctx.strokeStyle = grad;
        ctx.lineWidth = radius * 0.1;
        ctx.stroke();
        topCtx.beginPath();
        topCtx.arc(0, 0, radius * 0.1 + 2, 0, 2 * Math.PI);
        topCtx.fillStyle = '#595959';
        topCtx.strokeStyle = '#595959';
        topCtx.lineWidth = 1;
        topCtx.fill();
        topCtx.stroke();
    }

    function drawNumbers(ctx, radius) {
        ctx.font = radius * 0.15 + 'px arial';
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#ffffffAA';
        ctx.strokeStyle = '#ffffffAA';
        for (let num = 1; num < 13; num++) {
            const ang = num * Math.PI / 6;
            ctx.rotate(ang);
            ctx.translate(0, -radius * 0.77);
            ctx.rotate(-ang);
            ctx.fillText(num.toString(), 0, 0);
            ctx.rotate(ang);
            ctx.translate(0, radius * 0.77);
            ctx.rotate(-ang);
        }
        for (let num = 0; num < 60; num++) {
            const ang = num * Math.PI / 30;
            const isMajor = num % 5 === 0;
            ctx.beginPath();
            ctx.lineWidth = isMajor ? 3 : 1;
            ctx.lineCap = 'round';
            ctx.moveTo(0, 0);
            ctx.rotate(ang);
            ctx.moveTo(0, (radius * 0.95) - (isMajor ? radius * 0.08 : radius * 0.06));
            ctx.lineTo(0, radius * 0.95);
            ctx.stroke();
            ctx.rotate(-ang);
        }
    }

    function drawTime(hourCtx, minuteCtx, radius, currentDate) {
        const hour = currentDate.getHours() % 12;
        const minute = currentDate.getMinutes();
        const second = currentDate.getSeconds();
        const hourAngle = (hour * Math.PI / 6) + (minute * Math.PI / (6 * 60)) + (second * Math.PI / (360 * 60));
        drawHand(hourCtx, hourAngle, radius * 0.6, radius * 0.07, HOUR_COLOUR);
        const minuteAngle = (minute * Math.PI / 30) + (second * Math.PI / (30 * 60));
        drawHand(minuteCtx, minuteAngle, radius * 0.7, radius * 0.07, MINUTE_COLOUR);
    }

    function drawHand(ctx, pos, length, width, colour) {
        ctx.beginPath();
        ctx.lineWidth = width;
        ctx.lineCap = 'round';
        ctx.strokeStyle = colour;
        ctx.moveTo(0, 0);
        ctx.rotate(pos);
        ctx.lineTo(0, -length);
        ctx.stroke();
        ctx.rotate(-pos);
    }

    function getSeconds(currentDate) {
        const midnight = new Date(currentDate.getTime());
        midnight.setHours(0, 0, 0, 0);
        const offset = parameters.config.other_config.secondsDisplay === 'smooth' ? 1 : 0;
        return (currentDate.getTime() - midnight.getTime()) / 1000 + offset;
    }

    function toggleDelay() {
        const newInDelay = !inDelay;
        setInDelay(newInDelay);
        localStorage.setItem('inDelay_' + eventId, JSON.stringify(newInDelay));
        const msg = {
            sourceModuleName: 'ClockModule',
            event_grouping_seq: 0,
            customerID: parameters.userProfile.customer_id,
            siteID: parameters.userProfile.site_id,
            eventID: eventId,
            action: 'set_delay',
            delay: 0,
        };
        if (newInDelay) {
            const stored = localStorage.getItem('broadcastDelay_' + eventId);
            if (stored !== null) msg.delay = parseFloat(stored);
        }
        onSendMessage(msg);
    }

    const currentDate = GlobalFunction.getAdjustedDate(clockLocalOffset, parameters.config);
    const seconds = getSeconds(currentDate);
    const clockLeft = (containerWidth - canvasDiameter) / 2;
    const clockTop = (containerHeight - canvasDiameter) / 2;

    let transform = 'rotateZ(0deg)';
    let transition = '';
    if (seconds !== 0) {
        transform = 'rotateZ(' + seconds * 6 + 'deg)';
        if (parameters.config.other_config.secondsDisplay === 'smooth') transition = 'all 1s linear';
    }
    if (!tabHasFocus) transition = '';

    const showSecondHand = parameters.config.other_config.secondsDisplay === 'precise' ||
        parameters.config.other_config.secondsDisplay === 'smooth';

    const textShadow = '#333333 1px 1px 1px, #333333 1px 1px 2px, #333333 1px 1px 3px, #333333 1px 1px 0px, ' +
        '#333333 1px -1px 1px, #333333 1px -1px 2px, #333333 1px -1px 3px, #333333 1px -1px 0px, ' +
        '#333333 -1px 1px 1px, #333333 -1px 1px 2px, #333333 -1px 1px 3px, #333333 -1px 1px 0px, ' +
        '#333333 -1px -1px 1px, #333333 -1px -1px 2px, #333333 -1px -1px 3px, #333333 -1px -1px 0px';

    return (
        <div ref={containerRef} style={{ height: '100%', width: '100%', paddingLeft: 0, paddingRight: 0 }}>
            {showTimeMaster && (
                <TimeMaster
                    parameters={parameters}
                    onSendMessage={onSendMessage}
                    onExit={() => setShowTimeMaster(false)} />
            )}
            <div style={{ display: mode === 'setup' ? 'flex' : 'none', color: '#ffbe76', alignItems: 'center', paddingTop: '6px', paddingLeft: '6px' }}>
                <span style={{ marginTop: '2px' }}>Analog Clock Configure{' '}</span>
                <button onClick={() => setShowTimeMaster(true)} style={{ marginLeft: '6px', borderRadius: '50px', paddingLeft: '10px', paddingRight: '10px', paddingTop: '1px', paddingBottom: '1px', lineHeight: '1.2', fontSize: '11px', backgroundColor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', color: 'inherit', cursor: 'pointer' }}>Set</button>
            </div>

            <div style={{ position: 'absolute', left: clockLeft + 'px', top: clockTop + 'px', height: '100%' }}>
                <div style={{ position: 'relative', height: '100%' }}>
                    <canvas style={{ position: 'absolute', left: 0, top: 0, zIndex: 1 }} ref={clockCanvasRef} width={canvasDiameter} height={canvasDiameter} />
                    <canvas style={{ position: 'absolute', left: 0, top: 0, zIndex: 2 }} ref={hourCanvasRef} width={canvasDiameter} height={canvasDiameter} />
                    <canvas style={{ position: 'absolute', left: 0, top: 0, zIndex: 3 }} ref={minuteCanvasRef} width={canvasDiameter} height={canvasDiameter} />
                    <canvas style={{ position: 'absolute', left: 0, top: 0, zIndex: 0 }} ref={secondsCanvasRef} width={canvasDiameter} height={canvasDiameter} />

                    <div style={{
                        position: 'absolute', color: '#ffffff66', textShadow,
                        left: canvasDiameter * 0.24, top: canvasDiameter * 0.22,
                        height: canvasDiameter * 0.21 + 'px', width: canvasDiameter * 0.52 + 'px',
                        display: parameters.config.other_config.dateDisplayed !== '' ? 'block' : 'none',
                        textAlign: 'center', lineHeight: canvasDiameter * 0.1 + 'px', zIndex: 6,
                    }} ref={topLocationDivRef}>
                        <span style={{ fontSize: dowFontSize + 'px' }}>{dow}</span>
                        <br />
                        <span style={{ fontSize: calDateFontSize + 'px' }}>{calDate}</span>
                    </div>

                    <div style={{
                        position: 'absolute', color: 'white', textShadow,
                        left: canvasDiameter * 0.24, top: canvasDiameter * 0.57,
                        height: canvasDiameter * 0.19 + 'px', width: canvasDiameter * 0.52 + 'px',
                        display: parameters.config.other_config.timeInWordsDisplayed !== '' ? 'table' : 'none',
                        textAlign: 'center', lineHeight: part1FontSize + 'px', fontSize: part1FontSize + 'px', zIndex: 6,
                    }} ref={botLocationDivRef}>
                        <div style={{
                            height: canvasDiameter * 0.19 + 'px', width: canvasDiameter * 0.52 + 'px',
                            display: 'table-cell', textAlign: 'center', verticalAlign: 'middle',
                            lineHeight: part1FontSize + 'px', fontSize: part1FontSize + 'px',
                        }}>
                            {tiwPart1}
                        </div>
                    </div>

                    <canvas style={{ position: 'absolute', left: 0, top: 0, zIndex: 3 }} ref={topCanvasRef} width={canvasDiameter} height={canvasDiameter} />
                </div>

                {showSecondHand && (
                    <div className="seconds-container" style={{
                        position: 'absolute', left: 0, top: 0,
                        width: canvasDiameter + 'px', height: canvasDiameter + 'px',
                        zIndex: 4, display: mounted ? 'block' : 'none',
                        transform, transition,
                    }}>
                        <div className="seconds" ref={secondHandRef} style={{
                            position: 'absolute',
                            left: canvasDiameter / 2 - canvasDiameter * 0.005,
                            top: canvasDiameter * 0.05,
                            width: canvasDiameter * 0.01 + 'px',
                            height: canvasDiameter * 0.51 + 'px',
                            zIndex: 4, backgroundColor: 'red',
                            display: mounted ? 'block' : 'none',
                        }} />
                        <div className="secondsCircle" ref={secondHandCircleRef} style={{
                            position: 'absolute',
                            left: canvasDiameter * 0.5 - canvasDiameter * 0.05,
                            top: canvasDiameter * 0.5 - canvasDiameter * 0.05,
                            width: canvasDiameter * 0.1 + 'px',
                            height: canvasDiameter * 0.1 + 'px',
                            zIndex: 3, backgroundColor: 'white',
                            borderStyle: 'solid',
                            borderColor: parameters.config.currentTimeMaster === 'ME' ? 'red' : 'black',
                            borderWidth: '2px', borderRadius: '50%',
                            display: mounted ? 'block' : 'none',
                        }} />
                    </div>
                )}
            </div>

            <div style={{ display: parameters.config.currentTimeMaster === 'ME' && mode !== 'setup' && broadcastDelay > 0 ? 'block' : 'none' }}>
                <img
                    src={inDelay ? 'staticImages/InDelay.svg' : 'staticImages/OutDelay.svg'}
                    height={canvasDiameter * 0.08} width={canvasDiameter * 0.08}
                    className="cog"
                    onClick={toggleDelay}
                    style={{ zIndex: 5, position: 'fixed', top: clockTop + canvasDiameter * 0.18, left: clockLeft + canvasDiameter * 0.46 }} />
            </div>

            <div style={{ position: 'fixed', top: '350px', left: '-2000px', padding: '3px', backgroundColor: 'pink', whiteSpace: 'wrap' }} ref={fontSizeCheckerWrapRef} />
            <div style={{ position: 'fixed', top: '-3000px', left: '-2000px', backgroundColor: 'pink', whiteSpace: 'pre' }} ref={fontSizeCheckerPreRef} />
        </div>
    );
}

function toTitleCase(str) {
    return str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
}
